import type { FC } from "react";
import { memo, useContext, useEffect, useMemo, useState } from "react";

import TelemetryButton from "@/design-system/telemetry-button";
import type { ITransactionItem } from "@/src/cashback/MainPage/Transactions/shared";
import type { IUserCommunicationTemplate } from "@/src/common/UserCommunicationTemplate";
import type { IUserCommunicationTemplateModal } from "@/src/common/UserCommunicationTemplateModal";
import UserCommunicationTemplate from "@/src/common/UserCommunicationTemplateModal";
import { languageSelector, useViewportInfoContext } from "@/src/contexts";
import type { IComponent } from "@Components/Component";
import useAjax from "@Components/Hooks/useAjax";
import { Loading } from "@Components/Loading";
import {
    existInUrl,
    jumpToSectionId,
    lazyLoadComponentFactory,
    removeFromUrl,
    urlFormat,
} from "@Components/Modules/Helpers";
import {
    FaqLandingPage,
    allStoresPanelId,
    missingTransactionUrlParam,
    openTransactionsModal,
} from "@Constants/PortalConstants";
import { Cashback } from "@Routes/CashbackLinks";
import * as stylex from "@stylexjs/stylex";

import { type QuerySmMax, colors, globalFontStyles } from "../../../styles/tokens.stylex";
import type { ICashbackClaim } from "../CashbackClaim";
import { AvailabilityState } from "../CashbackCommonInterfaces";
import { CashbackDashboardContext } from "./CashbackDashboardContext";
import { CashbackMainPageResources } from "./CashbackMainPage.Resources";
import { CashbackMainPageResourcesEn } from "./CashbackMainPage.Resources.En";
import type { IScrollPagination } from "./ScrollPagination";
import type { ITransactionList } from "./TransactionList";
import TransactionList from "./TransactionList";
import { TransactionsContext } from "./TransactionsContext";
import type { ComplaintType } from "./../CashbackCommonInterfaces"

import * as css from "./TransactionManager.module.css";

const CashbackClaim = lazyLoadComponentFactory(
    () => import(/* webpackChunkName: "cashback-claim" */ "../CashbackClaim"),
);

const TransactionListModal = lazyLoadComponentFactory(
    () => import(/* webpackChunkName: "transaction-list-modal" */ "./TransactionListModal"),
);

export interface ITransactionManager extends IComponent {
    transactionList: ITransactionList;
    cashbackClaim: ICashbackClaim;
}

interface IValidateComplaint { action:string, transactionId:string, complaintType: ComplaintType }

const TransactionManager: FC<ITransactionManager> = memo(
    (props) => {
        const { setUpdateTransactions, cleanUpdateTransactions } = useContext(CashbackDashboardContext);
        const { isMobileOrTablet } = useViewportInfoContext();

        const [showClaimModal, setShowClaimModal] = useState<boolean>(false);
        const [warningAboutComplain, setWarningAboutComplain] =
            useState<IUserCommunicationTemplateModal | null>();
        const [transaction, setTransaction] = useState<ITransactionItem | null>(null);
        const [showTransactionsModal, setShowTransactionsModal] = useState(false);

        const mainResources = languageSelector(CashbackMainPageResources, CashbackMainPageResourcesEn);
        const resources = mainResources.transactionsManager;

        const { request: validateComplaint, isLoading } = useAjax<IUserCommunicationTemplate | null>({
            ajaxAction: urlFormat(
                Cashback,
                {
                    action: "ValidateComplaint",
                    transactionId: transaction?.id,
                    complaintType: transaction?.availability === AvailabilityState.Rejected ? "CashbackRejected" : "IncorrectCashback",
                } satisfies IValidateComplaint,
                true,
            ),
            success: (response: IUserCommunicationTemplate) => {
                if (response) {
                    const modalCustom = {
                        commBase: response,
                        modal: {
                            closeCustomTelemetry: { eventName: "common-exit" },
                            onClose: () => setWarningAboutComplain(null),
                        },
                    } as IUserCommunicationTemplateModal;

                    setWarningAboutComplain(modalCustom);

                    if (transaction) {
                        setTransaction(null);
                    }
                    return;
                }
                setShowClaimModal(true);
            },
        });

        const validateComponent = (id: string) => {
            validateComplaint({ id });
        };

        useEffect(() => {
            if (transaction) {
                validateComponent(transaction.id);
            }
        }, [transaction]);

        const shouldShowClaimModal = () => {
            if (showTransactionsModal) {
                setShowTransactionsModal(false);
            }

            if (transaction) {
                validateComponent(transaction.id);
                return;
            }
            setTransaction(null);
            setShowClaimModal(true);
        };

        const closeClaim = (lastComponent: boolean): void => {
            setShowClaimModal(false);

            if (lastComponent) {
                setUpdateTransactions();
                return;
            }

            cleanUpdateTransactions();
            setTransaction(null);
        };

        const claimProps = (): JSX.Element | null => {
            if (!transaction) {
                return (
                    <CashbackClaim
                        {...props.cashbackClaim}
                        navigationModal={{
                            ...props.cashbackClaim.navigationModal,
                            onClose: closeClaim,
                        }}
                    />
                );
            }

            return (
                <CashbackClaim
                    {...props.cashbackClaim}
                    fields={{
                        transactionId: transaction.id,
                        date: transaction.date,
                        brandName: transaction.brandName,
                        brandId: transaction.brandId,
                        purchaseAmount: transaction.purchaseAmount,
                        cashbackAmount: transaction.returnedAmount,
                        orderId: transaction.orderId,
                        availability: transaction.availability,
                        imageUrl: transaction.logo,
                    }}
                    navigationModal={{
                        ...props.cashbackClaim.navigationModal,
                        onClose: closeClaim,
                    }}
                />
            );
        };

        const transactionList = (pageSize: number, noScroll: boolean) => (
            <TransactionList
                {...props.transactionList}
                transPagination={
                    {
                        pageSize: pageSize,
                        noScrollLoading: noScroll,
                    } as IScrollPagination
                }
                shouldShowModal={shouldShowClaimModal}
            />
        );

        const value = useMemo(
            () => ({
                claim: setTransaction,
            }),
            [setTransaction],
        );

        const onCloseModalTransactions = (toggle: boolean) => {
            cleanUpdateTransactions();
            setShowTransactionsModal(toggle);
        };

        useEffect(() => {
            const timer = setTimeout(() => {
                if (existInUrl(openTransactionsModal)) {
                    onCloseModalTransactions(true);
                    removeFromUrl(openTransactionsModal);
                }
            }, 500);

            if (existInUrl(missingTransactionUrlParam)) {
                setShowClaimModal(true);
                removeFromUrl(missingTransactionUrlParam);
            }

            return () => clearTimeout(timer);
        }, []);

        return (
            <div className={css.transactionManagerComponent}>
                <TransactionsContext.Provider value={value}>
                    {transactionList(3, true)}

                    <div {...stylex.props(styles.buttonsContainer)}>
                        {!isMobileOrTablet && (
                            <div>
                                <TelemetryButton
                                    as="button"
                                    variant="magentaRound"
                                    size="M"
                                    onClick={() => jumpToSectionId(allStoresPanelId, true)}
                                    eventName="cshbck-AllBrands"
                                    styleX={[globalFontStyles.BodyM, styles.allStoreButton]}
                                >
                                    {mainResources.transactionsManager.allShopsText}
                                </TelemetryButton>
                                <TelemetryButton
                                    as="a"
                                    variant="transparent"
                                    size="L"
                                    href={FaqLandingPage}
                                    styleX={styles.faqButton}
                                    eventName="cshbck-Faq}"
                                    isExternal
                                >
                                    {mainResources.transactionsManager.faqText}
                                </TelemetryButton>
                            </div>
                        )}

                        <TelemetryButton
                            as="button"
                            variant="transparent"
                            size="M"
                            onClick={() => onCloseModalTransactions(true)}
                            eventName="cshbck-AllTransactions"
                        >
                            {resources.showAllText}
                        </TelemetryButton>
                    </div>
                    {warningAboutComplain && <UserCommunicationTemplate {...warningAboutComplain} />}
                    {showTransactionsModal && (
                        <TransactionListModal
                            className={css.modalTransactionList}
                            onClose={() => onCloseModalTransactions(false)}
                        >
                            <div
                                className={`${css.modalTransactionListWrapper} ${stylex.props(styles.border).className}`}
                            >
                                <div className={css.title}>{resources.modalTitle}</div>
                                {transactionList(10, false)}
                            </div>
                        </TransactionListModal>
                    )}

                    {isLoading && <Loading className={stylex.props(styles.loading).className} isModal />}
                    {showClaimModal && claimProps()}
                </TransactionsContext.Provider>
            </div>
        );
    },
    (prev, next) => {
        return prev.cashbackClaim === next.cashbackClaim || prev.key === next.key;
    },
);

const smMax: QuerySmMax = "@media (max-width: 989px)";

const styles = stylex.create({
    buttonsContainer: {
        alignItems: "center",
        display: "flex",
        justifyContent: { default: "space-between", [smMax]: "center" },
    },

    faqButton: {
        color: colors.fg_BrandMagenta,
        fontWeight: "bold",
    },

    allStoreButton: {
        marginBottom: 5,
    },

    loading: {
        alignItems: "center",
    },

    border: {
        borderRadius: "1.5625rem",
    },
});

export default TransactionManager;
